import moment from 'moment';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

export type RangePreset = '30d' | '90d' | '12m' | 'all';

export const RANGE_PRESETS: { value: RangePreset; label: string; short: string }[] = [
  { value: '30d', label: 'Last 30 days', short: '30D' },
  { value: '90d', label: 'Last 90 days', short: '90D' },
  { value: '12m', label: 'Last 12 months', short: '12M' },
  { value: 'all', label: 'All time', short: 'All' },
];

// ENG-5256 product decision: default range = last 12 months
const DEFAULT_PRESET: RangePreset = '12m';

export interface ResolvedRange {
  preset: RangePreset;
  /**
   * RFC3339 lower bound passed to `group.stats(start_date:)`. Null for all-time
   * (no lower bound — the backend returns the full history).
   */
  start_date: string | null;
  /** RFC3339 upper bound (exclusive) passed to `group.stats(end_date:)` — now. */
  end_date: string;
  /** Preset display label (e.g. "Last 12 months", "All time"). */
  label: string;
  /** Compact label for the range control (e.g. "12M", "All"). */
  short: string;
}

function isRangePreset(value: string | null): value is RangePreset {
  return value === '30d' || value === '90d' || value === '12m' || value === 'all';
}

/** Pure resolution of a preset into concrete RFC3339 bounds. */
export function resolveRange(preset: RangePreset, now: moment.Moment = moment()): ResolvedRange {
  const meta = RANGE_PRESETS.find((p) => p.value === preset) ?? RANGE_PRESETS[2];
  const end = now.clone();

  if (preset === 'all') {
    return {
      preset,
      start_date: null,
      end_date: end.toISOString(true),
      label: meta.label,
      short: meta.short,
    };
  }

  const start =
    preset === '30d'
      ? end.clone().subtract(30, 'days')
      : preset === '90d'
        ? end.clone().subtract(90, 'days')
        : end.clone().subtract(12, 'months');

  return {
    preset,
    start_date: start.toISOString(true),
    end_date: end.toISOString(true),
    label: meta.label,
    short: meta.short,
  };
}

/**
 * Dashboard date-range state, persisted in the URL (`?range=`) per the app's
 * search-param convention. Defaults to the last 12 months (ENG-5256 product
 * decision). "All" stays selectable and resolves to an unbounded window.
 */
export function useDateRange(): { preset: RangePreset; setPreset: (preset: RangePreset) => void } {
  const [params, setParams] = useSearchParams();
  const raw = params.get('range');
  const preset = isRangePreset(raw) ? raw : DEFAULT_PRESET;

  const setPreset = useCallback(
    (next: RangePreset) => {
      const updated = new URLSearchParams(params);
      if (next === DEFAULT_PRESET) {
        updated.delete('range');
      } else {
        updated.set('range', next);
      }
      setParams(updated, { replace: true });
    },
    [params, setParams]
  );

  return { preset, setPreset };
}
